import { createContext, type ReactNode, use } from "react";

import type {
    ChatArticle,
    ChatAttachment,
    ChatMessageProps,
    ChatReaction,
    ConfirmedBooking,
    DeliveryStatus,
} from "./types";

type MessageContextValue = Omit<
    ChatMessageProps,
    | "showSenderRow"
    | "deliveryStatus"
    | "attachments"
    | "confirmedBookings"
    | "articles"
    | "reaction"
> & {
    showSenderRow: boolean;
    deliveryStatus: DeliveryStatus;
    attachments: ChatAttachment[];
    confirmedBookings: ConfirmedBooking[];
    articles: ChatArticle[];
    reaction: ChatReaction | null;
};

const MessageContext = createContext<MessageContextValue | null>(null);

type MessageProviderProps = {
    children: ReactNode;
    message: ChatMessageProps;
};

export function MessageProvider({ children, message }: MessageProviderProps) {
    return (
        <MessageContext
            value={{
                ...message,
                showSenderRow: message.showSenderRow ?? true,
                deliveryStatus: message.deliveryStatus ?? "sent",
                attachments: message.attachments ?? [],
                confirmedBookings: message.confirmedBookings ?? [],
                articles: message.articles ?? [],
                reaction: message.reaction ?? null,
            }}
        >
            {children}
        </MessageContext>
    );
}

export function useMessage() {
    const message = use(MessageContext);

    if (!message) {
        throw new Error("Message components must be rendered within MessageProvider");
    }

    return message;
}
